import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { api } from "@/lib/api";
import { SiteHeader } from "@/components/SiteHeader";
import { Check, Clock, Coffee, Hourglass, Store, Truck } from "lucide-react";

const statusLabel = {
  pending: "Awaiting confirmation",
  confirmed: "Confirmed",
  brewing: "Brewing",
  ready: "Ready",
  completed: "Delivered / Picked up",
  cancelled: "Cancelled",
};
const statusColor = {
  pending: "#D69F4C",
  confirmed: "#7A8B76",
  brewing: "#C05A46",
  ready: "#7A8B76",
  completed: "#1A1514",
  cancelled: "#7B7573",
};

export default function OrderSuccess() {
  const { orderNumber } = useParams();
  const [order, setOrder] = useState(null);
  const [err, setErr] = useState("");

  const load = async () => {
    try {
      const { data } = await api.get(`/orders/lookup/${orderNumber}`);
      setOrder(data);
    } catch { setErr("We couldn't find that order."); }
  };
  useEffect(() => {
    load();
    const iv = setInterval(load, 15000);
    return () => clearInterval(iv);
  }, [orderNumber]);

  const isDelivery = order?.fulfillment_type === "delivery";

  return (
    <div data-testid="order-success-page" className="min-h-screen grain-overlay">
      <SiteHeader />
      <main className="max-w-3xl mx-auto px-6 md:px-12 py-16">
        <div className="text-center">
          <div className="mx-auto inline-flex h-14 w-14 items-center justify-center rounded-full bg-[#D69F4C] text-white">
            <Hourglass className="h-7 w-7" strokeWidth={1.75} />
          </div>
          <p className="label-eyebrow mt-6">Order received</p>
          <h1 className="mt-3 font-serif-display text-4xl sm:text-5xl tracking-tighter">
            Awaiting confirmation from The Cozy Bean.
          </h1>
          <p className="mt-3 text-[#4A4341] max-w-lg mx-auto">
            I&rsquo;ll review your order and confirm it shortly. Your order number is{" "}
            <span data-testid="order-number" className="font-mono-jet text-[#C05A46]">{orderNumber}</span>.
            You can bookmark this page — the status updates live.
          </p>
        </div>

        {err ? (
          <p className="mt-10 text-center text-[#C05A46]">{err}</p>
        ) : !order ? (
          <p className="mt-10 text-center text-[#7B7573]">Loading order…</p>
        ) : (
          <div className="mt-10 selectable-card p-8">
            <div className="flex flex-wrap items-center justify-between gap-4">
              <div>
                <p className="label-eyebrow text-[#4A4341]">Status</p>
                <p data-testid="order-status" className="mt-1 font-serif-display text-2xl inline-flex items-center gap-2">
                  <span className="inline-block h-2.5 w-2.5 rounded-full" style={{ backgroundColor: statusColor[order.status] }} />
                  {statusLabel[order.status] || order.status}
                </p>
              </div>
              <div className="text-right">
                <p className="label-eyebrow text-[#4A4341]">{isDelivery ? "Delivery" : "Pickup"}</p>
                <p className="mt-1 font-serif-display text-xl inline-flex items-center gap-2 flex-wrap justify-end">
                  {isDelivery ? <Truck className="h-4 w-4 text-[#C05A46]" /> : <Store className="h-4 w-4 text-[#7A8B76]" />}
                  <span>{order.pickup_date}</span>
                  <span>·</span>
                  <Clock className="h-4 w-4" />
                  <span>{order.pickup_time}</span>
                </p>
              </div>
            </div>

            {order.status === "pending" && (
              <div data-testid="pending-notice" className="mt-6 rounded-lg border border-[#D69F4C]/40 bg-[#D69F4C]/10 p-4">
                <p className="label-eyebrow text-[#1A1514]">Not yet confirmed</p>
                <p className="mt-1 text-sm text-[#1A1514]">
                  I&rsquo;ll confirm the exact {isDelivery ? "delivery time and destination" : "pickup time and spot"} shortly.
                  This page will update as soon as it&rsquo;s confirmed.
                </p>
              </div>
            )}

            {order.status !== "pending" && order.confirmation_message ? (
              <div data-testid="confirmation-message" className="mt-6 rounded-lg border border-[#7A8B76]/40 bg-[#7A8B76]/10 p-4">
                <p className="label-eyebrow text-[#1A1514] inline-flex items-center gap-1"><Check className="h-3.5 w-3.5" /> Confirmed</p>
                <p className="mt-1 text-sm text-[#1A1514]">{order.confirmation_message}</p>
              </div>
            ) : null}

            {isDelivery && (order.delivery_group || order.delivery_address) ? (
              <div className="mt-6">
                <p className="label-eyebrow text-[#4A4341]">Delivering to</p>
                <p className="mt-1 text-sm font-medium">{order.delivery_group || order.delivery_address}</p>
                {order.delivery_group && order.delivery_address ? (
                  <p className="mt-0.5 text-xs text-[#7B7573]">{order.delivery_address}</p>
                ) : null}
              </div>
            ) : null}

            {!isDelivery ? (
              <div data-testid="pickup-address-notice" className="mt-6 rounded-lg border border-[#D69F4C]/40 bg-[#D69F4C]/10 p-4">
                <p className="label-eyebrow text-[#1A1514]">Pickup address</p>
                <p className="mt-1 text-sm text-[#1A1514]">
                  {order.status === "pending"
                    ? "I'll text you the pickup address once I confirm your order."
                    : (order.delivery_address || order.confirmation_message
                        ? "See your confirmation message for the pickup address."
                        : "I'll text you the pickup address shortly.")}
                </p>
              </div>
            ) : null}

            <div className="mt-6 border-t border-black/10 pt-6">
              <p className="label-eyebrow text-[#4A4341] mb-3">Items</p>
              <ul className="space-y-3">
                {order.items.map((line, i) => (
                  <li key={i} className="flex items-start justify-between gap-4">
                    <div>
                      <p className="font-medium">{line.quantity}× {line.drink.name}{" "}
                        <span className="text-xs text-[#7B7573]">{line.size}</span></p>
                      <p className="text-xs text-[#4A4341]">
                        {[line.milk?.name, ...line.syrups.map((s) => s.name), ...line.extras.map((e) => e.name)]
                          .filter(Boolean).join(" · ") || "As-is"}
                      </p>
                    </div>
                    <span className="font-mono-jet text-sm">${Number(line.line_total).toFixed(2)}</span>
                  </li>
                ))}
              </ul>
            </div>

            <div className="mt-6 border-t border-black/10 pt-6 flex items-center justify-between">
              <span className="label-eyebrow text-[#4A4341]">Total due on arrival</span>
              <span className="font-serif-display text-2xl">${Number(order.total).toFixed(2)}</span>
            </div>
          </div>
        )}

        <div className="mt-10 flex flex-wrap items-center justify-center gap-4">
          <Link to="/order" data-testid="order-again-btn"
            className="btn-brand rounded-full px-6 py-3 text-sm font-semibold inline-flex items-center gap-2">
            <Coffee className="h-4 w-4" /> Order again
          </Link>
          <Link to="/" data-testid="back-home-btn"
            className="rounded-full border border-black/15 px-6 py-3 text-sm font-semibold hover:border-[#1A1514] transition-colors">
            Back home
          </Link>
        </div>
      </main>
    </div>
  );
}
